import type React from 'react';

import { Divider, FormField, Input, Label, Textarea } from '@zillow/constellation-10';

import type { Section } from '../../../models';
import { ELECTRIC_VEHICLE_CHARGING } from '../../../constants';
import YesAndNoExpansion from '../../../../shared/components/Widgets/YesAndNoExpansion';
import CurrencyInput from '../../Widgets/CurrencyInput';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function ElectricVehicleCharging({ section, setSectionValues }: Props) {
  function onNoElectricVehicleCharging(): void {
    setSectionValues({
      [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.IS_ENABLED]: false,
    });
  }

  function onYesElectricVehicleCharging(): void {
    setSectionValues({
      [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.IS_ENABLED]: true,
    });
  }

  function onNoPersonalLiabilityInsurance(): void {
    setSectionValues({
      [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.PERSONAL_LIABILITY_INSURANCE]: false,
    });
  }

  function onYesPersonalLiabilityInsurance(): void {
    setSectionValues({
      [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.PERSONAL_LIABILITY_INSURANCE]: true,
    });
  }

  function onNoSeparatelyMetered(): void {
    setSectionValues({
      [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.SEPARATELY_METERED]: false,
    });
  }

  function onYesSeparatelyMetered(): void {
    setSectionValues({
      [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.SEPARATELY_METERED]: true,
    });
  }

  function onInstallationUseMaintenanceChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.INSTALLATION_USE_MAINTENANCE]: value });
  }

  function onPersonalLiabilityAmountChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.PERSONAL_LIABILITY_INSURANCE_AMOUNT]: event.currentTarget.value,
    });
  }

  function onServiceProviderChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  return (
    <FormField
      required
      label={
        <Label>Is the tenant permitted to install and maintain an electric charging station on the property?</Label>
      }
      control={
        <YesAndNoExpansion
          yesHandler={onYesElectricVehicleCharging}
          noHandler={onNoElectricVehicleCharging}
          yesActive={section.getFieldValue(ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.IS_ENABLED)}
        >
          <FormField
            required
            css={{ marginBottom: 'layout.tight' }}
            label={<Label>Is personal liability insurance required?</Label>}
            control={
              <YesAndNoExpansion
                yesHandler={onYesPersonalLiabilityInsurance}
                noHandler={onNoPersonalLiabilityInsurance}
                yesActive={section.getFieldValue(ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.PERSONAL_LIABILITY_INSURANCE)}
              >
                <FormField
                  required
                  label={<Label id="Estimated-association-fees">Personal liability insurance coverage amount</Label>}
                  control={
                    <CurrencyInput
                      onChange={onPersonalLiabilityAmountChange}
                      onBlur={onPersonalLiabilityAmountChange}
                      value={section.getFieldValue(
                        ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.PERSONAL_LIABILITY_INSURANCE_AMOUNT,
                      )}
                    />
                  }
                />
              </YesAndNoExpansion>
            }
          />
          <Divider css={{ marginTop: 'layout.tight', marginBottom: 'layout.tight' }} />
          <FormField
            required
            css={{ marginBottom: 'layout.tight' }}
            label={<Label>Is the electric vehicle charging station separately metered?</Label>}
            control={
              <YesAndNoExpansion
                yesHandler={onYesSeparatelyMetered}
                noHandler={onNoSeparatelyMetered}
                yesActive={section.getFieldValue(ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.SEPARATELY_METERED)}
              >
                <FormField
                  required
                  label={<Label>What service provider?</Label>}
                  control={
                    <Input
                      onChange={onServiceProviderChange}
                      value={section.getFieldValue(ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.SERVICE_PROVIDER)}
                    />
                  }
                />
              </YesAndNoExpansion>
            }
          />
          <Divider css={{ marginTop: 'layout.tight', marginBottom: 'layout.tight' }} />
          <FormField
            optional
            label={
              <Label>
                What requirements do you have for installation, use, and maintenance of the electric vehicle station?
              </Label>
            }
            control={
              <Textarea
                value={section.getFieldValue(ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.INSTALLATION_USE_MAINTENANCE)}
                onChange={onInstallationUseMaintenanceChange}
                css={{ resize: 'vertical' }}
              />
            }
          />
        </YesAndNoExpansion>
      }
    />
  );
}

ElectricVehicleCharging.description = 'Hack week electric vehicle charging question';

export default ElectricVehicleCharging;
